<template>
  <div class="call-form">
    <div v-if="submitError" class="call-form__error">
      {{ $t("call.form.errors.submitFailed") }}
    </div>

    <div class="call-form__field">
      <label class="call-form__label">{{ $t("call.form.service") }}</label>
      <Select
        :model-value="form.queueType || undefined"
        :disabled="submitting"
        @update:model-value="onQueueTypeChange"
      >
        <SelectTrigger class="w-full" :size="formFieldSize">
          <SelectValue :placeholder="$t('call.form.selectService')" />
        </SelectTrigger>
        <SelectContent class="z-[2100]">
          <SelectItem v-for="q in queueTypes" :key="q.code" :value="q.code">
            {{ localizedLabel(q) }}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div class="call-form__field">
      <label class="call-form__label">{{
        $t("call.form.yourPhoneNumber")
      }}</label>
      <Input
        :model-value="form.principalPhoneNumber"
        :disabled="submitting"
        :size="formFieldSize"
        type="tel"
        inputmode="tel"
        autocomplete="tel"
        maxlength="16"
        placeholder="+7-7XX-XXX-XX-XX"
        @update:model-value="onPhoneInput"
        @focus="onPhoneFocus"
      />
    </div>

    <div v-if="showAttorneyIin" class="call-form__field">
      <label class="call-form__label">{{ attorneyIinLabel }}</label>
      <Input
        v-model="form.attorneyIin"
        :disabled="submitting"
        :size="formFieldSize"
        maxlength="12"
        inputmode="numeric"
        @update:model-value="onAttorneyIinInput"
        @blur="onAttorneyIinBlur"
      />
      <div v-if="attorneyIinChecking" class="call-form__field-hint">
        {{ $t("call.form.checkingIin") }}
      </div>
      <div v-else-if="attorneyIinFormatError" class="call-form__field-error">
        {{ $t("call.form.errors.iinFormat") }}
      </div>
      <div v-else-if="attorneyIinSameAsSelf" class="call-form__field-error">
        {{ $t("call.form.errors.iinSameAsSelf") }}
      </div>
      <div
        v-else-if="attorneyIinServerErrorText"
        class="call-form__field-error"
      >
        {{ attorneyIinServerErrorText }}
      </div>
    </div>

    <div class="call-form__field">
      <label class="call-form__label">{{ $t("call.form.organization") }}</label>
      <Select
        :model-value="form.organizationId ?? undefined"
        :disabled="submitting"
        @update:model-value="onOrganizationChange"
      >
        <SelectTrigger class="w-full" :size="formFieldSize">
          <SelectValue :placeholder="$t('call.form.selectOrganization')" />
        </SelectTrigger>
        <SelectContent class="z-[2100]">
          <SelectItem v-for="o in organizations" :key="o.id" :value="o.id">
            {{ localizedLabel(o) }}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div class="call-form__field">
      <label class="call-form__label">{{ $t("call.form.notary") }}</label>
      <Select
        :model-value="form.notaryUuid || undefined"
        :disabled="submitting || form.organizationId == null || loadingNotaries"
        @update:model-value="onNotaryChange"
      >
        <SelectTrigger class="w-full" :size="formFieldSize">
          <SelectValue
            :placeholder="
              loadingNotaries
                ? $t('call.form.loading')
                : $t('call.form.selectNotary')
            "
          />
        </SelectTrigger>
        <SelectContent class="z-[2100]">
          <SelectItem v-for="n in notaries" :key="n.uuid" :value="n.uuid">
            <span class="inline-flex items-center gap-2">
              <span
                class="size-2 rounded-full shrink-0"
                :class="n.is_online ? 'bg-green-500' : 'bg-red-500'"
                :title="n.is_online ? 'Online' : 'Offline'"
              />
              {{ n.label }}
            </span>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>

    <Button
      class="call-form__submit"
      :disabled="!canSubmit || submitting"
      :size="formFieldSize"
      @click="onSubmit"
    >
      <Spinner v-if="submitting" class="mr-2 h-4 w-4" />
      {{ $t("call.form.submit") }}
    </Button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/shared/ui/select";
import { Input } from "@/shared/ui/input";
import Button from "@/shared/ui/button/Button.vue";
import { Spinner } from "@/shared/ui/spinner";
import { useIsMobile } from "@/shared/lib/composables/useIsMobile";
import { useCallWindow } from "../model/useCallWindow";
import { useCallForm } from "../model/useCallForm";
import {
  fetchQueueTypes,
  fetchOrganizations,
  fetchNotaries,
} from "../model/mockCallApi";

const store = useStore();
const { locale, t } = useI18n();
const { setCallSession } = useCallWindow();
const {
  form,
  submitting,
  submitError,
  isIinValid,
  isFormValid,
  attorneyIinRequired,
  attorneyIinChecking,
  attorneyIinServerError,
  resetAttorneyIinValidation,
  checkAttorneyIin,
  submitForm,
} = useCallForm();

const queueTypes = ref([]);
const organizations = ref([]);
const notaries = ref([]);
const loadingNotaries = ref(false);
const attorneyIinTouched = ref(false);

const selfIin = computed(() => store.getters.get_self_user?.identifier || "");

const selectedQueueType = computed(() =>
  queueTypes.value.find((q) => q.code === form.queueType)
);
const showAttorneyIin = computed(() => attorneyIinRequired());
const attorneyIinLabel = computed(() => {
  const q = selectedQueueType.value;
  if (!q) return t("call.form.attorneyIin");
  if (locale.value === "kk" && q.attorney_iin_label_kz) {
    return q.attorney_iin_label_kz;
  }
  return q.attorney_iin_label || t("call.form.attorneyIin");
});
const attorneyIinFormatError = computed(
  () =>
    showAttorneyIin.value &&
    attorneyIinTouched.value &&
    !!form.attorneyIin &&
    !isIinValid(form.attorneyIin)
);
const attorneyIinSameAsSelf = computed(
  () =>
    showAttorneyIin.value &&
    isIinValid(form.attorneyIin) &&
    !!selfIin.value &&
    form.attorneyIin === String(selfIin.value)
);
const attorneyIinServerErrorText = computed(() => {
  if (!showAttorneyIin.value) return "";
  if (attorneyIinSameAsSelf.value) return "";
  const err = attorneyIinServerError.value;
  if (!err) return "";
  if (err === "sameAsSelf") return t("call.form.errors.iinSameAsSelf");
  if (err === "invalid") return t("call.form.errors.iinInvalid");
  return err;
});
const canSubmit = computed(() => isFormValid());

const { isMobile } = useIsMobile();

const formFieldSize = computed(() => (isMobile.value ? "lg" : "default"));

function localizedLabel(item) {
  return locale.value === "kk" && item.label_kz ? item.label_kz : item.label;
}

function onQueueTypeChange(code) {
  form.queueType = code;
  const q = queueTypes.value.find((qt) => qt.code === code);
  form.requiredFields = q?.required_fields || [];
  if (!attorneyIinRequired()) {
    form.attorneyIin = "";
    attorneyIinTouched.value = false;
    resetAttorneyIinValidation();
  }
}

function normalizePhone(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  const trimmed =
    digits.startsWith("7") || digits.startsWith("8") ? digits.slice(1) : digits;
  const tail = trimmed.slice(0, 10);
  let out = "+7";
  if (tail.length > 0) out += "-" + tail.slice(0, 3);
  if (tail.length > 3) out += "-" + tail.slice(3, 6);
  if (tail.length > 6) out += "-" + tail.slice(6, 8);
  if (tail.length > 8) out += "-" + tail.slice(8, 10);
  return out;
}

function onPhoneInput(value) {
  form.principalPhoneNumber = normalizePhone(value);
}

function onPhoneFocus() {
  if (!form.principalPhoneNumber) {
    form.principalPhoneNumber = "+7";
  }
}

function onAttorneyIinInput() {
  resetAttorneyIinValidation();
}

async function onAttorneyIinBlur() {
  attorneyIinTouched.value = true;
  if (!isIinValid(form.attorneyIin)) return;
  await checkAttorneyIin(selfIin.value);
}

async function onOrganizationChange(code) {
  form.organizationId = code;
  form.notaryUuid = null;
  notaries.value = [];
  if (!code) return;
  loadingNotaries.value = true;
  try {
    notaries.value = await fetchNotaries(code);
  } finally {
    loadingNotaries.value = false;
  }
}

function onNotaryChange(uuid) {
  form.notaryUuid = uuid;
}

async function onSubmit() {
  if (showAttorneyIin.value) {
    attorneyIinTouched.value = true;
    const ok = await checkAttorneyIin(selfIin.value);
    if (!ok) return;
  }
  if (!canSubmit.value) return;
  const selfUser = store.getters.get_self_user || {};
  const result = await submitForm({
    principalIin: selfUser.identifier,
  });
  if (result) {
    setCallSession(result);
  }
}

onMounted(async () => {
  if (!form.principalPhoneNumber) {
    const selfUser = store.getters.get_self_user || {};
    if (selfUser.phone) {
      form.principalPhoneNumber = normalizePhone(selfUser.phone);
    }
  }
  const [q, o] = await Promise.all([fetchQueueTypes(), fetchOrganizations()]);
  queueTypes.value = q;
  organizations.value = o;
  if (form.organizationId) {
    notaries.value = await fetchNotaries(form.organizationId);
  }
});
</script>

<style lang="scss" scoped>
.call-form {
  @apply flex flex-col gap-3 p-4 overflow-y-auto w-full mx-auto;
  flex: 1;
  max-width: 900px;
}

.call-form__error {
  @apply text-sm rounded-md p-2;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.call-form__field {
  @apply flex flex-col gap-1;
}

.call-form__label {
  @apply text-xs font-medium;
  color: hsl(var(--foreground));
}

.call-form__field-error {
  @apply text-xs;
  color: #ef4444;
}

.call-form__field-hint {
  @apply text-xs;
  color: hsl(var(--muted-foreground));
}

.call-form__submit {
  @apply mt-2;
}

@media (max-width: 900px) {
  .call-form {
    @apply gap-5 py-6;
  }

  .call-form__submit {
    margin-top: auto;
  }
}
</style>
